import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useCaptionButtonScrim } from '@/layout/hooks/useCaptionButtonScrim';
import { getWindowChrome } from '../model/windowChrome';
import { useUiScaleStore } from '../store/uiScaleStore';

export { getWindowChrome } from '../model/windowChrome';

/**
 * Fullscreen is the one part of the frame that DOES change at runtime: macOS
 * auto-hides the traffic lights, so the inset reserved for them has to collapse
 * or the first control floats 86px from the window edge for the whole
 * fullscreen session. Windows self-corrects via `env(titlebar-area-width)`.
 */
function useIsFullscreen(): boolean {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => window.electronAPI?.windowChrome?.onFullscreenChanged?.(setIsFullscreen), []);

  return isFullscreen;
}

/**
 * Mirror the frame's shape onto the ROOT element.
 *
 * The attributes below already sit on this component's own node, which is all
 * the toolbar's own rules need. Modal surfaces are portalled to `<body>` —
 * outside this subtree — and they have their own business with the frame on
 * Windows, where the caption buttons are composited above the page: a dialog
 * has to stay clear of the band those buttons occupy, and its scrim must not
 * fade, because the buttons it dims in step with are repainted in one step by
 * the main process. Both rules live in src/index.css and select on these
 * mirrored attributes.
 */
function useMirroredWindowChrome(mode: string, platform: string): void {
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.windowChrome = mode;
    root.dataset.windowPlatform = platform;
    return () => {
      delete root.dataset.windowChrome;
      delete root.dataset.windowPlatform;
    };
  }, [mode, platform]);
}

interface IntegratedTitlebarProps {
  children: ReactNode;
  className?: string;
}

export function IntegratedTitlebar({ children, className }: IntegratedTitlebarProps) {
  const chrome = getWindowChrome();
  const isFullscreen = useIsFullscreen();
  // The Windows caption buttons are painted by the OS on top of this row, so a
  // dialog's scrim cannot dim them from the page. Mounted here because this is
  // the component that owns that strip.
  useCaptionButtonScrim();
  const isIntegrated = chrome.mode === 'integrated';
  // `contentInsetLeft` is in screen points: the traffic lights are drawn
  // outside the page and keep their size whatever the interface scale is, so
  // the CSS padding that clears them has to grow as the page shrinks.
  const uiScale = useUiScaleStore((state) => state.scale) || 1;
  const insetLeft = isFullscreen ? 0 : chrome.contentInsetLeft / uiScale;
  useMirroredWindowChrome(chrome.mode, chrome.platform);

  return (
    <div
      data-testid="integrated-titlebar"
      data-window-chrome={chrome.mode}
      data-window-platform={chrome.platform}
      data-window-fullscreen={isFullscreen ? 'true' : 'false'}
      className={cn('relative', isIntegrated && 'app-region-drag', className)}
      style={
        isIntegrated ? ({ '--titlebar-inset-left': `${insetLeft}px` } as CSSProperties) : undefined
      }
    >
      {children}
    </div>
  );
}
